/**
 * The two things a plan change has to carry across the Stripe round trip.
 *
 * Stripe Checkout's SuccessUrl is server-fixed and drops our query params, so
 * neither can travel in the URL. Both live in localStorage, are consumed once,
 * and expire, so a stale value from an abandoned checkout cannot fire later.
 *
 * 1. The return URL: where the user was when a wall stopped them. Written by
 *    `ChangePlan` on arrival (from `?returnUrl=`), read once the change commits.
 * 2. The landing record: which plan they just moved to. Written by `ChangePlan`
 *    as it reloads or redirects, read by `PlanUpgradeConfirmation` on the page
 *    they land on, so the confirmation survives the full navigation.
 */

const RETURN_URL_KEY = "billing_return_url";
const LANDING_KEY = "plan_upgrade_landing";

const RETURN_URL_TTL_MS = 60 * 60 * 1000;
const LANDING_TTL_MS = 10 * 60 * 1000;

/** A same-origin path (`/products?import=csv`), never a scheme or protocol-relative URL. */
export const isInternalPath = (url: unknown): url is string =>
  typeof url === "string" && url.startsWith("/") && !url.startsWith("//");

/**
 * The page a wall should send the user back to when its caller named none:
 * where they are now, query included, unless that is the billing area itself
 * (returning a user to the page they just paid on is the behaviour this replaces).
 */
export const defaultReturnUrl = (location: {
  pathname: string;
  search?: string;
}): string | null => {
  if (location.pathname.startsWith("/account/billing")) return null;
  return `${location.pathname}${location.search ?? ""}`;
};

export function rememberBillingReturnUrl(url: string): void {
  if (!isInternalPath(url)) return;
  try {
    localStorage.setItem(RETURN_URL_KEY, JSON.stringify({ url, ts: Date.now() }));
  } catch {
    // Storage unavailable: the user lands on the billing page, as before.
  }
}

/** Read and clear the return URL. Null when none, malformed, external, or older than an hour. */
export function consumeBillingReturnUrl(): string | null {
  try {
    const raw = localStorage.getItem(RETURN_URL_KEY);
    if (!raw) return null;
    localStorage.removeItem(RETURN_URL_KEY);
    const { url, ts } = JSON.parse(raw) as { url?: unknown; ts?: number };
    if (!isInternalPath(url)) return null;
    if (!ts || Date.now() - ts > RETURN_URL_TTL_MS) return null;
    return url;
  } catch {
    return null;
  }
}

export type PlanUpgradeLanding = {
  /** Plan display names as the server reports them ("Lite", "Pro"). */
  from: string;
  to: string;
  /** True when the user was sent back to the page they upgraded from, not to billing. */
  returned: boolean;
  ts: number;
};

export function stashPlanUpgradeLanding(
  landing: Omit<PlanUpgradeLanding, "ts">,
): void {
  try {
    localStorage.setItem(
      LANDING_KEY,
      JSON.stringify({ ...landing, ts: Date.now() }),
    );
  } catch {
    // No confirmation, nothing else changes.
  }
}

/**
 * True while a landing record is waiting to be shown. Does not consume it: `Start` asks before
 * `PlanUpgradeConfirmation` takes the record, to resume onboarding for a user who paid mid-way.
 */
export function hasPlanUpgradeLanding(): boolean {
  try {
    const raw = localStorage.getItem(LANDING_KEY);
    if (!raw) return false;
    const landing = JSON.parse(raw) as PlanUpgradeLanding;
    return (
      typeof landing?.to === "string" &&
      !!landing.ts &&
      Date.now() - landing.ts <= LANDING_TTL_MS
    );
  } catch {
    return false;
  }
}

/** Read and clear the landing record. Null when none or older than ten minutes. */
export function takePlanUpgradeLanding(): PlanUpgradeLanding | null {
  try {
    const raw = localStorage.getItem(LANDING_KEY);
    if (!raw) return null;
    localStorage.removeItem(LANDING_KEY);
    const landing = JSON.parse(raw) as PlanUpgradeLanding;
    if (typeof landing?.to !== "string" || !landing.ts) return null;
    if (Date.now() - landing.ts > LANDING_TTL_MS) return null;
    return landing;
  } catch {
    return null;
  }
}
